"use client";

import Link from "next/link";
import React from "react";

type SmartLinkProps = React.PropsWithChildren<{
  href: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  target?: string;
  rel?: string;
}>;

function toRelative(href: string): string {
  if (!href) return "./index.html";
  if (href === "/") return "./index.html";
  if (href.startsWith("/")) return `.${href}`;
  return href;
}

function ensureIndexHtml(href: string): string {
  if (/\/$/.test(href)) return `${href}index.html`;
  return href;
}

function computeRootPrefixFromLocation(): string {
  try {
    const pathname = window.location?.pathname || "";
    const outIdx = pathname.lastIndexOf("/out/");
    if (outIdx === -1) return ""; // fallback: nothing
    const afterOut = pathname.substring(outIdx + "/out/".length);
    // Remove filename if present
    const parts = afterOut.split("/").filter(Boolean);
    if (parts.length === 0) return ""; // already at out/
    // If last part is a file (has a dot), do not count it for depth
    const last = parts[parts.length - 1];
    const isFile = /\./.test(last);
    const depth = isFile ? parts.length - 1 : parts.length;
    if (depth <= 0) return "";
    return "../".repeat(depth);
  } catch {
    return "";
  }
}

export default function SmartLink({ href, children, className, ...rest }: SmartLinkProps) {
  const isFile = typeof window !== "undefined" && window.location?.protocol === "file:";

  if (isFile) {
    if (href.startsWith("/")) {
      // Construire un lien relatif depuis la racine 'out/'
      const prefix = computeRootPrefixFromLocation();
      let normalized = href.replace(/^\//, ""); // remove leading '/'
      if (normalized === "") normalized = "index.html";
      if (/\/$/.test(normalized)) normalized += "index.html";
      const finalAbs = `${prefix}${normalized}`;
      return (
        <a href={finalAbs} className={className} {...rest}>
          {children}
        </a>
      );
    }
    // Chemins déjà relatifs
    const relative = toRelative(href);
    const finalHref = ensureIndexHtml(relative);
    return (
      <a href={finalHref} className={className} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className} {...rest}>
      {children}
    </Link>
  );
} 